import React, { useContext } from 'react';
import { IoMdArrowDropdownCircle, IoMdArrowDropupCircle } from 'react-icons/io';
import getDeviceTypeMobile from '../selectors/getDeviceTypeMobile';
import Banner from './Banner';
import { useAppSelector } from '../app/hooks';
import combineStyles from '../utils/combineStyles';
import { CONTAINER_PROPERTIES, TEXT_PROPERTIES } from '../utils/theme';
import getThemeLookup from '../selectors/getThemeLookup';
import { getQuantityDisplay } from '../libs/polygon-utils/src/ChoiceSet';
import type { INestedChoiceSetsContext } from './NestedChoiceSetsContext';
import { NestedChoiceSetsContext } from './NestedChoiceSetsContext';
import { useTranslation } from 'react-i18next';

interface IProps {
  choiceSet: ValidatedChoiceSet;
  collapsed?: boolean;
  toggleCollapsed: (id: string) => void;
  hideCaret?: boolean;
}

const NestedChoiceSetBanner: React.FC<IProps> = ({
  choiceSet,
  collapsed,
  toggleCollapsed,
  hideCaret = false,
}) => {
  const p = useAppSelector(getThemeLookup);
  const { t } = useTranslation();

  const isMobileDevice = useAppSelector(getDeviceTypeMobile);
  const collapsableChoiceSetsEnabled = useAppSelector(
    state => state.config.collapsableChoiceSetsEnabled,
  );

  const { stages } = useContext(NestedChoiceSetsContext) as INestedChoiceSetsContext;

  const leftLabel = choiceSet.name;

  const rightLabel = getQuantityDisplay({
    choiceSet,
    stages,
    requiredText: t('requiredText', 'REQUIRED'),
    optionalText: t('optionalText', 'OPTIONAL'),
  });

  const labelTextSize = { fontSize: isMobileDevice ? 12 : 14 };

  const getLabelIconComponent = () => {
    if (hideCaret) {
      return undefined;
    }

    if (!collapsableChoiceSetsEnabled) {
      return undefined;
    }

    if (collapsed) {
      return IoMdArrowDropupCircle;
    }

    return IoMdArrowDropdownCircle;
  };

  return (
    <Banner
      sticky
      LabelIconComponent={getLabelIconComponent()}
      labelIconStyle={p('choiceSetBannerToggle', CONTAINER_PROPERTIES)}
      leftLabel={leftLabel}
      rightLabel={choiceSet.displayType === 'makeameal' ? undefined : `${rightLabel}`}
      leftLabelStyle={combineStyles(
        styles.leftLabel,
        labelTextSize,
        p('orderingBannerHeading', TEXT_PROPERTIES),
      )}
      rightLabelStyle={combineStyles(labelTextSize, p('choiceSetBannerRightText', TEXT_PROPERTIES))}
      containerStyle={styles.container}
      problem={!choiceSet.valid}
      onClick={collapsableChoiceSetsEnabled ? () => toggleCollapsed(choiceSet.id) : undefined}
    />
  );
};

const styles: Styles = {
  emphasized: {
    fontWeight: 900,
  },
  container: {
    zIndex: 5, // to be higher than textShadows inside sibling choice set components
    borderRightWidth: 0,
    borderLeftWidth: 0,
    borderBottomWidth: 0,
    backgroundColor: 'transparent',
    position: 'relative',
  },
  leftLabel: {
    textTransform: 'uppercase',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
  },
};

export default NestedChoiceSetBanner;
